/**
 * Utilitários de telefone brasileiro usados por todos os modos do discador
 * (mobile, inline/min e flutuante) e pela API de histórico.
 *
 * Regras (Plano de Numeração ANATEL):
 * - Fixo: 8 dígitos após o DDD, começando com 2, 3, 4 ou 5. NUNCA recebe o 9.
 * - Celular: 9 dígitos após o DDD, começando com 9.
 * - Celular no formato antigo (8 dígitos começando com 6, 7, 8 ou 9): recebe o
 *   nono dígito (9) na frente.
 *
 * Nenhuma função de FORMATAÇÃO altera a sequência de dígitos; só a
 * normalização (colar/ligar) pode acrescentar DDD padrão ou o nono dígito.
 */
import { isValidPhoneNumber as isValidPhoneNumberMax, parsePhoneNumberFromString } from 'libphonenumber-js/max';

/** DDD assumido quando o número colado/digitado não tem DDD (comportamento histórico do widget). */
export const DEFAULT_DDD = '48';
export const BR_COUNTRY_CODE = '55';

export type BrazilianLineType = 'landline' | 'mobile' | 'unknown';

/** Remove tudo que não for dígito. */
export function onlyDigits(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).replace(/\D/g, '');
}

/**
 * Números não geográficos (0800, 0300, 0500, 0900): "0" de discagem + prefixo
 * + 6 ou 7 dígitos. A forma nacional canônica é sem o 0 ("8001234567").
 * Não há ambiguidade com DDD: 30, 50, 80 e 90 não existem.
 */
const NON_GEOGRAPHIC_RE = /^0?((?:800|300|500|900)\d{6,7})$/;

/** Devolve a forma canônica (sem o 0) de um número 0800/0300/0500/0900, ou null. */
export function matchNonGeographicNumber(value: string): string | null {
  const match = onlyDigits(value).match(NON_GEOGRAPHIC_RE);
  return match ? match[1] : null;
}

/**
 * Classifica a parte LOCAL (sem DDD) de um número brasileiro.
 * - 8 dígitos começando com 2-5  -> fixo
 * - 8 dígitos começando com 6-9  -> celular antigo (precisa do 9)
 * - 9 dígitos começando com 9    -> celular
 */
export function classifyLocalNumber(local: string): BrazilianLineType {
  const digits = onlyDigits(local);

  if (digits.length === 8) {
    if (/^[2-5]/.test(digits)) {
      return 'landline';
    }
    if (/^[6-9]/.test(digits)) {
      return 'mobile';
    }
    return 'unknown';
  }

  if (digits.length === 9) {
    return digits.startsWith('9') ? 'mobile' : 'unknown';
  }

  return 'unknown';
}

/**
 * Remove prefixos de discagem de um número nacional: zeros à esquerda
 * (0 de discagem / 00 internacional) e o código do país (55) quando ele
 * precede um número completo de 10 ou 11 dígitos.
 */
export function stripCountryCode(value: string): string {
  let digits = onlyDigits(value);

  // DDD nunca começa com 0: zeros à esquerda só podem ser prefixo de discagem.
  while (digits.startsWith('0') && digits.length > 10) {
    digits = digits.slice(1);
  }

  if (digits.startsWith(BR_COUNTRY_CODE) && (digits.length === 12 || digits.length === 13)) {
    digits = digits.slice(2);
  }

  return digits;
}

/**
 * Separa DDD e parte local de um número nacional (10 ou 11 dígitos).
 */
export function splitBrazilianNumber(value: string): { ddd: string; local: string } | null {
  const digits = stripCountryCode(value);
  if (digits.length !== 10 && digits.length !== 11) {
    return null;
  }
  return { ddd: digits.slice(0, 2), local: digits.slice(2) };
}

/** Tipo de linha de um número nacional (10/11 dígitos), pelas regras estruturais. */
export function getLineType(value: string): BrazilianLineType {
  const parts = splitBrazilianNumber(value);
  if (!parts) {
    return 'unknown';
  }
  return classifyLocalNumber(parts.local);
}

export function isLandlineNumber(value: string): boolean {
  return getLineType(value) === 'landline';
}

export function isMobileNumber(value: string): boolean {
  const parts = splitBrazilianNumber(value);
  return Boolean(parts && parts.local.length === 9 && classifyLocalNumber(parts.local) === 'mobile');
}

/**
 * Códigos de seleção de prestadora (CSP) aceitos no formato "0 + CSP + DDD + número".
 * Sem o 0 inicial os mesmos dígitos são ambíguos (21, 31, 41... também são DDDs).
 */
export const CARRIER_SELECTION_CODES = ['12', '14', '15', '17', '21', '23', '25', '31', '41', '43'];

export interface NormalizePhoneOptions {
  /**
   * DDD acrescentado quando o número vem sem DDD (8 ou 9 dígitos).
   * `null` desativa o complemento de DDD.
   */
  defaultDDD?: string | null;
  /**
   * Quais celulares no formato antigo (8 dígitos) recebem o nono dígito:
   * - 'all' (padrão; colar/ligar): locais começando com 6, 7, 8 ou 9
   * - 'unambiguous' (digitação): só 6, 7 e 8 - um local começando com 9 pode ser
   *   um celular de 9 dígitos ainda incompleto
   */
  legacyMobile?: 'all' | 'unambiguous';
}

/**
 * Normaliza um número brasileiro digitado/colado em qualquer formato para
 * `DDD + número` (10 dígitos para fixo, 11 para celular), pronto para ligar.
 *
 * Acrescenta o nono dígito APENAS em celulares no formato antigo
 * (8 dígitos começando com 6-9). Fixos (2-5) são mantidos com 8 dígitos.
 *
 * Exemplos:
 *   "(51) 3248-8509"       -> "5132488509"   (fixo, sem 9)
 *   "+55 (48) 9203-3200"   -> "48992033200"  (celular antigo, ganha o 9)
 *   "48 99203-3200"        -> "48992033200"
 *   "0 21 48 99203 3200"   -> "48992033200"  (0 + operadora + DDD)
 */
export function normalizeBrazilianPhone(input: string, options: NormalizePhoneOptions = {}): string {
  const defaultDDD = options.defaultDDD === undefined ? DEFAULT_DDD : options.defaultDDD;
  const legacyMobile = options.legacyMobile ?? 'all';
  const rawDigits = onlyDigits(input);
  const startsWithTrunkZero = rawDigits.startsWith('0');

  // 0800/0300/0500/0900: forma canônica sem o 0, antes de qualquer regra de DDD
  const nonGeographic = matchNonGeographicNumber(rawDigits);
  if (nonGeographic) {
    return nonGeographic;
  }

  let digits = stripCountryCode(rawDigits);

  if (!digits) {
    return '';
  }

  const nonGeographicAfterCountry = matchNonGeographicNumber(digits);
  if (nonGeographicAfterCountry) {
    return nonGeographicAfterCountry;
  }

  if (digits.length > 13) {
    // Entrada que não é um telefone (vários números, "ramal", lixo): não
    // adivinha um número; devolve como está para a validação bloquear.
    return digits;
  }

  if (digits.length === 12 || digits.length === 13) {
    // Só "0 + CSP + DDD + número" é reconhecido (o 0 já foi removido acima).
    // Sem o 0 inicial ou com CSP desconhecido a entrada é ambígua
    // (ex.: "(11) 3248-8509 ramal 12") e fica como está, inválida.
    const csp = digits.slice(0, 2);
    if (!startsWithTrunkZero || !CARRIER_SELECTION_CODES.includes(csp)) {
      return digits;
    }
    digits = digits.slice(2);
  }

  const needsNinthDigit = (local: string) =>
    classifyLocalNumber(local) === 'mobile' && (legacyMobile === 'all' || !local.startsWith('9'));

  if (digits.length === 11) {
    // DDD + 9 dígitos. Se o terceiro dígito não for 9 o número é inválido,
    // mas não "consertamos" para não ligar para o número errado.
    return digits;
  }

  if (digits.length === 10) {
    const ddd = digits.slice(0, 2);
    const local = digits.slice(2);
    return needsNinthDigit(local) ? `${ddd}9${local}` : digits;
  }

  if (digits.length === 9) {
    if (digits.startsWith('9') && defaultDDD) {
      return `${defaultDDD}${digits}`;
    }
    return digits;
  }

  if (digits.length === 8) {
    const type = classifyLocalNumber(digits);
    if (!defaultDDD || type === 'unknown') {
      return digits;
    }
    return needsNinthDigit(digits) ? `${defaultDDD}9${digits}` : `${defaultDDD}${digits}`;
  }

  return digits;
}

/**
 * Nome histórico da normalização usada no evento de colar e ao ligar.
 * Mantido para compatibilidade com os componentes existentes.
 */
export function normalizePhoneNumberPaste(pastedText: string): string {
  return normalizeBrazilianPhone(pastedText);
}

/**
 * Formata um número para exibição de forma progressiva, SEM alterar os dígitos.
 *   "5132488509"   -> "(51) 3248-8509"   (fixo)
 *   "51993248850"  -> "(51) 99324-8850"  (celular)
 *   "513248"       -> "(51) 3248"        (digitando)
 *   "5551..."      -> "+55 (51) ..."     (com código do país)
 * Valores com * ou # (DTMF) são exibidos como estão.
 */
export function formatPhoneForDisplay(phoneNumber: string): string {
  if (!phoneNumber) {
    return '';
  }

  const raw = String(phoneNumber);
  if (/[*#]/.test(raw)) {
    return raw;
  }

  let digits = onlyDigits(raw);
  if (!digits) {
    return raw;
  }

  let prefix = '';
  const hasPlus = raw.trim().startsWith('+');
  if (digits.startsWith(BR_COUNTRY_CODE) && (hasPlus || digits.length >= 12) && digits.length <= 13) {
    prefix = '+55 ';
    digits = digits.slice(2);
  }

  if (digits.length <= 2) {
    return `${prefix}${digits}`;
  }

  // 0800 123 4567 (o 0 só aparece se foi digitado: a máscara nunca cria dígitos)
  const nonGeo = digits.match(/^(0?)((?:800|300|500|900))(\d{0,7})$/);
  if (nonGeo) {
    const rest = nonGeo[3];
    const tail = rest.length <= 3 ? rest : `${rest.slice(0, 3)} ${rest.slice(3)}`;
    return `${prefix}${nonGeo[1]}${nonGeo[2]}${tail ? ` ${tail}` : ''}`;
  }

  const ddd = digits.slice(0, 2);
  const local = digits.slice(2);

  if (local.length <= 4) {
    return `${prefix}(${ddd}) ${local}`;
  }

  if (local.length <= 8) {
    return `${prefix}(${ddd}) ${local.slice(0, 4)}-${local.slice(4)}`;
  }

  if (local.length === 9) {
    return `${prefix}(${ddd}) ${local.slice(0, 5)}-${local.slice(5)}`;
  }

  return `${prefix}${digits}`;
}

/**
 * Formata um número armazenado (ex.: "+5551932488509" vindo da API) no padrão
 * nacional "(51) 93248-8509", sem acrescentar nem remover dígitos do número.
 */
export function formatNationalPhone(value: string): string {
  const raw = (value ?? '').trim();
  const allDigits = onlyDigits(raw);
  // Número internacional de outro país (+1..., +351...): mostra como veio.
  if (raw.startsWith('+') && !allDigits.startsWith(BR_COUNTRY_CODE)) {
    return raw;
  }
  const digits = stripCountryCode(raw);
  if (!digits) {
    return raw;
  }
  if (digits.length === 10 || digits.length === 11) {
    return formatPhoneForDisplay(digits);
  }
  return raw;
}

/**
 * Validação estrutural de um número nacional já normalizado:
 * - 10 dígitos: DDD válido + fixo (2-5)
 * - 11 dígitos: DDD válido + celular (9)
 */
export function isValidPhoneFormat(phoneNumber: string): boolean {
  const digits = stripCountryCode(phoneNumber);
  if (matchNonGeographicNumber(digits)) {
    return true;
  }
  if (digits.length === 10) {
    return /^[1-9][1-9][2-5]\d{7}$/.test(digits);
  }
  if (digits.length === 11) {
    return /^[1-9][1-9]9\d{8}$/.test(digits);
  }
  return false;
}

/** Converte um número nacional (10/11 dígitos) para E.164 (+55...). */
export function toE164(value: string): string | null {
  const trimmed = (value ?? '').trim();
  if (trimmed.startsWith('+')) {
    const international = onlyDigits(trimmed);
    return international ? `+${international}` : null;
  }
  const digits = stripCountryCode(trimmed);
  const nonGeographic = matchNonGeographicNumber(digits);
  if (nonGeographic) {
    return `+${BR_COUNTRY_CODE}${nonGeographic}`;
  }
  if (digits.length !== 10 && digits.length !== 11) {
    return null;
  }
  return `+${BR_COUNTRY_CODE}${digits}`;
}

/**
 * Validação completa (metadados "max" do libphonenumber): DDD existente,
 * faixa de fixo/celular correta, tamanho certo. Rejeita celular de 8 dígitos
 * sem o 9 e DDD inexistente. Atenção: um fixo que já recebeu um 9 indevido
 * (ex.: "(51) 93248-8509") é estruturalmente um celular válido, e por isso
 * NÃO é detectável aqui - a proteção está em nunca acrescentar o 9 a fixos.
 */
export function isValidE164PhoneNumber(e164: string | null | undefined): boolean {
  if (!e164) {
    return false;
  }
  try {
    return isValidPhoneNumberMax(e164);
  } catch {
    return false;
  }
}

/** Validação completa de um número nacional brasileiro (10/11 dígitos). */
export function isValidBrazilianPhoneNumber(value: string): boolean {
  const e164 = toE164(value);
  return e164 ? e164.startsWith(`+${BR_COUNTRY_CODE}`) && isValidE164PhoneNumber(e164) : false;
}

/** Tipo de linha segundo o libphonenumber (metadados completos). */
export function getPhoneNumberType(value: string): BrazilianLineType {
  const e164 = toE164(value);
  if (!e164) {
    return 'unknown';
  }
  try {
    const parsed = parsePhoneNumberFromString(e164);
    const type = parsed?.getType();
    if (type === 'FIXED_LINE') {
      return 'landline';
    }
    if (type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE') {
      return 'mobile';
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
}
